/**
 * Where a "Back" button goes (N1).
 *
 * **The bug it fixes.** A FixyBee opening one of their own quotes from "My
 * quotes" lands on the job page — `/provider/jobs/:id`, the same screen a
 * browsing FixyBee reaches from the board — and Back put them on the board.
 * The list they came from was gone, along with their filter and their scroll
 * position, and the screen they arrived at was one they had not asked for.
 *
 * **Why not `navigate(-1)`.** It is the obvious fix and it is the one this
 * codebase already paid for once (the original CIERRE, B3): history is not the
 * app's to reason about. A person who arrived from an email, a push
 * notification or a pasted link has no previous entry inside FixyDany, and
 * `-1` walks them out of the product — or, worse, back into a redirect that
 * bounces them forward again and traps the button. A reload replaces the
 * entry; an inline registration pushes two.
 *
 * So the destination is always explicit. The surface that links in says where
 * it is linking from, and the page reads that; when nothing said, a rule that
 * does not depend on history decides. Both halves are needed — the second one
 * is what makes the first optional, so a caller that forgets is a slightly
 * worse Back and never a broken one.
 *
 * Usage, from the linking surface:
 *
 *     <Link to={jobDetailPath(role, id)} state={from("/provider/quotes")}>
 *
 * and on the page:
 *
 *     const back = backTarget(location, fallback);
 */

/** The router state a linking surface attaches. */
export function from(path) {
  return { from: path };
}

/**
 * The path to go back to: what the caller said, else `fallback`.
 *
 * Only in-app absolute paths are honoured. A `from` that is not one — an
 * absolute URL, a protocol-relative `//evil.example`, anything a query string
 * could have carried — is dropped in favour of the fallback, because this
 * value ends up in `navigate()` and router state is only as trustworthy as
 * whoever pushed it.
 */
export function backTarget(location, fallback) {
  const said = location?.state?.from;
  if (typeof said !== "string") return fallback;
  if (!said.startsWith("/") || said.startsWith("//")) return fallback;
  return said;
}
